/**
 * JourneyMapView - Unified diagram-first Zero Trust assessment
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  ZoomIn, ZoomOut, RotateCcw, X, ArrowRight, ArrowLeft,
  Check, Key, Laptop, Layers, Database, Users, Server, Shield,
  Network, Eye, Settings, CheckCircle, Circle, HelpCircle, Info,
  Fingerprint, KeyRound, UserCog, ShieldAlert, MonitorSmartphone,
  ShieldCheck, Lock, Globe, FileKey, FolderLock,
  Package, ChevronRight, ChevronDown, AlertTriangle, Zap, DollarSign
} from 'lucide-react';
import { useSpendIndicators, selectVendorIndicator } from '../../../hooks/useSpendIndicators';
import { useSuggestions } from '../../../hooks/useSuggestions';
import { useZtProductCatalog } from '../../../hooks/useZtProductCatalog';
import TransactionListModal from '../../../assessment/TransactionListModal';
import { SAMPLE_PRODUCTS } from '../../ArchitectureDiagram/PillarTemplate';
import './JourneyMapView.css';

// Build product suggestions from SAMPLE_PRODUCTS (150+ products vs old hardcoded 40)
// Maps capability codes to SAMPLE_PRODUCTS categories across all pillars
// Returns structured data for proper spend lookup
const buildProductSuggestions = () => {
  const allProducts = [
    ...SAMPLE_PRODUCTS.IDENTITY,
    ...SAMPLE_PRODUCTS.DEVICES,
    ...SAMPLE_PRODUCTS.NETWORKS,
    ...SAMPLE_PRODUCTS.APPLICATIONS,
    ...SAMPLE_PRODUCTS.DATA
  ];

  // Map JourneyMapView capability codes to SAMPLE_PRODUCTS categories
  const categoryMapping = {
    mfa: ['mfa'],
    sso: ['idp', 'access'],
    idp: ['idp', 'directory'],
    edr: ['edr'],
    siem: ['analytics'],
    ztna: ['ztna', 'segmentation'],
    casb: ['ztna'], // CASB often bundled with ZTNA
    dlp: ['dlp', 'classification', 'discovery']
  };

  const suggestions = {};
  Object.entries(categoryMapping).forEach(([capCode, categories]) => {
    const seen = new Set();
    const products = allProducts
      .filter(p => categories.includes(p.category))
      .filter(p => {
        const key = `${p.name}|${p.product}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(p => ({
        vendor: p.name,
        product: p.product,
        display: p.product, // Use just product name for cleaner display
        value: `${p.name}|${p.product}` // Structured value for parsing
      }));
    suggestions[capCode] = products;
  });

  return suggestions;
};

const PRODUCT_SUGGESTIONS = buildProductSuggestions();

// Parse a product value back into vendor/product
const parseProductValue = (value) => {
  if (!value) return { vendor: null, product: null };
  const parts = value.split('|');
  return { vendor: parts[0] || null, product: parts[1] || null };
};

// Map JourneyMapView function codes to CISA function codes for shared state sync
const FUNCTION_CODE_MAP = {
  // Identity pillar
  'AUTH': 'ID_AUTH',
  'IDENTITY_STORES': 'ID_STORES',
  'ACCESS': 'ID_ACCESS',
  // Devices pillar
  'DEVICE_HEALTH': 'DEV_POLICY',
  'THREAT_PROTECT': 'DEV_THREAT',
  // Networks pillar
  'SEGMENTATION': 'NET_SEG',
  'ENCRYPTION': 'NET_ENCRYPT',
  // Applications pillar
  'APP_ACCESS': 'APP_ACCESS',
  'APP_SECURITY': 'APP_SECURITY',
  // Data pillar
  'DATA_PROTECTION': 'DATA_PROTECTION',
  'DATA_ACCESS': 'DATA_ACCESS'
};

const MATURITY_LEVELS = [
  { key: 'TRADITIONAL', label: 'Traditional', color: 'var(--zt-traditional)' },
  { key: 'INITIAL', label: 'Initial', color: 'var(--zt-initial)' },
  { key: 'ADVANCED', label: 'Advanced', color: 'var(--zt-advanced)' },
  { key: 'OPTIMAL', label: 'Optimal', color: 'var(--zt-optimal)' }
];

// Product-specific upgrade guidance
const UPGRADE_GUIDANCE = {
  'Microsoft Authenticator': {
    toAdvanced: {
      canUpgrade: true,
      steps: [
        'Enable Passwordless sign-in in Azure AD portal',
        'Configure Authenticator for FIDO2 passkey support (requires iOS 16+ / Android 9+)',
        'Enable Number Matching and Additional Context in Authentication Methods',
        'Disable SMS/Voice fallback for privileged accounts'
      ],
      effort: 'Configuration change - no new product needed',
      timeline: '2-4 weeks for phased rollout'
    },
    toOptimal: {
      canUpgrade: false,
      steps: [
        'Integrate with Microsoft Entra ID Protection for risk-based auth',
        'Enable Conditional Access with Sign-in Risk policies',
        'Deploy Microsoft Sentinel for ML-driven anomaly detection',
        'Configure session lifetime policies and continuous access evaluation (CAE)'
      ],
      additionalProducts: ['Microsoft Entra ID P2', 'Microsoft Sentinel'],
      effort: 'Requires Entra ID P2 license upgrade',
      timeline: '1-3 months'
    }
  },
  'Duo Security': {
    toAdvanced: {
      canUpgrade: true,
      steps: [
        'Deploy Duo Security Keys or enable WebAuthn in Duo Admin Panel',
        'Configure Verified Duo Push with number matching',
        'Enable Risk-Based Authentication in Duo Trust Monitor',
        'Require hardware tokens for admin accounts'
      ],
      effort: 'Configuration + possible hardware purchase',
      timeline: '2-4 weeks'
    },
    toOptimal: {
      canUpgrade: true,
      steps: [
        'Enable Duo Trust Monitor for continuous risk assessment',
        'Integrate with SIEM for real-time risk correlation',
        'Configure adaptive policies based on device trust + user behavior',
        'Implement step-up auth triggers for anomalous activity'
      ],
      effort: 'Requires Duo Advantage or Premier edition',
      timeline: '1-2 months'
    }
  },
  'Okta Verify': {
    toAdvanced: {
      canUpgrade: true,
      steps: [
        'Enable FastPass for phishing-resistant passwordless auth',
        'Deploy FIDO2 WebAuthn with Okta Verify or security keys',
        'Configure Device Trust policies in Okta Admin',
        'Disable legacy MFA methods for sensitive applications'
      ],
      effort: 'Configuration change',
      timeline: '2-3 weeks'
    },
    toOptimal: {
      canUpgrade: true,
      steps: [
        'Enable Okta ThreatInsight for ML-based threat detection',
        'Configure Behavior Detection policies',
        'Integrate with Okta Identity Governance for continuous access review',
        'Implement session anomaly detection and auto-revocation'
      ],
      additionalProducts: ['Okta Identity Governance'],
      effort: 'May require additional Okta modules',
      timeline: '1-2 months'
    }
  },
  'Google Authenticator': {
    toAdvanced: {
      canUpgrade: false,
      needsProductChange: true,
      steps: [
        'Google Authenticator does NOT support FIDO2/phishing-resistant MFA',
        'Migrate to: Titan Security Keys, YubiKey, or Passkeys',
        'If using Google Workspace: Enable Passkeys in Admin Console',
        'Consider Duo or Okta for enterprise features'
      ],
      effort: 'Product change required',
      timeline: '1-2 months for migration'
    },
    toOptimal: {
      canUpgrade: false,
      needsProductChange: true,
      steps: [
        'Requires enterprise MFA solution with adaptive capabilities',
        'Recommended: Duo Premier, Okta Adaptive MFA, or Microsoft Entra P2',
        'Implement SIEM integration for behavioral analytics'
      ],
      effort: 'Full platform migration needed',
      timeline: '2-4 months'
    }
  },
  'YubiKey': {
    toAdvanced: {
      canUpgrade: true,
      alreadyMeets: true,
      steps: [
        'YubiKey already provides FIDO2/phishing-resistant MFA',
        'Ensure FIDO2/WebAuthn is enabled (not just OTP mode)',
        'Register keys with your IdP using WebAuthn protocol',
        'Enforce hardware key requirement for privileged accounts'
      ],
      effort: 'Configuration verification',
      timeline: '1-2 weeks'
    },
    toOptimal: {
      canUpgrade: false,
      steps: [
        'YubiKey provides strong auth but not adaptive/continuous',
        'Add risk-based layer: Microsoft Entra ID Protection or Duo Trust Monitor',
        'Integrate with SIEM for session monitoring',
        'Configure step-up requirements based on risk signals'
      ],
      additionalProducts: ['Risk-based auth platform', 'SIEM/UEBA'],
      effort: 'Add complementary risk platform',
      timeline: '1-2 months'
    }
  },
  'Entrust Grid Card (Legacy)': {
    toAdvanced: {
      canUpgrade: false,
      needsProductChange: true,
      isLegacy: true,
      steps: [
        'Grid cards are NOT phishing-resistant (Traditional-level MFA)',
        'Grid card systems are vulnerable to man-in-the-middle attacks',
        'Migrate to: FIDO2 security keys, Duo, or Okta Verify',
        'Consider YubiKey for offline-capable phishing-resistant auth',
        'Plan gradual rollout starting with privileged accounts'
      ],
      effort: 'Full MFA platform migration required',
      timeline: '3-6 months for enterprise-wide transition'
    },
    toOptimal: {
      canUpgrade: false,
      needsProductChange: true,
      isLegacy: true,
      steps: [
        'Grid cards cannot support adaptive or continuous authentication',
        'Requires modern IdP with ML-driven risk assessment',
        'Recommended: Duo Premier, Okta Adaptive MFA, or Microsoft Entra P2',
        'Implement SIEM integration for behavioral analytics',
        'Configure step-up auth based on real-time risk signals'
      ],
      additionalProducts: ['Modern MFA platform', 'Risk-based auth engine', 'SIEM/UEBA'],
      effort: 'Complete platform replacement + risk engine',
      timeline: '4-8 months'
    }
  },
  'default': {
    toAdvanced: {
      steps: [
        'Deploy phishing-resistant MFA (FIDO2 security keys or PIV cards)',
        'Enable passwordless authentication where supported',
        'Disable fallback to SMS/voice for sensitive accounts',
        'Require hardware tokens for privileged access'
      ],
      effort: 'Varies by current solution'
    },
    toOptimal: {
      steps: [
        'Implement risk-based/adaptive authentication',
        'Integrate with SIEM/UEBA for behavioral analytics',
        'Enable continuous session validation',
        'Configure automated step-up auth on risk detection'
      ],
      effort: 'Requires enterprise security platform'
    }
  }
};

const PILLAR_ASSESSMENTS = {
  IDENTITY: {
    label: 'Identity',
    icon: Key,
    color: 'var(--zt-identity)',
    functions: [
      {
        code: 'AUTH',
        name: 'Authentication',
        icon: Fingerprint,
        description: 'How users prove identity',
        capabilities: [
          {
            id: 'auth_mfa',
            text: 'Multi-Factor Authentication (MFA)',
            level: 'INITIAL',
            productCategory: 'mfa',
            requirements: ['MFA enforced for all users', '2+ factors required', 'Applied to cloud & on-prem apps']
          },
          {
            id: 'auth_phishing',
            text: 'Phishing-Resistant MFA (FIDO2, PIV)',
            level: 'ADVANCED',
            productCategory: 'mfa',
            requirements: ['FIDO2 security keys or PIV cards', 'Passwordless enabled', 'Hardware tokens for admins']
          },
          {
            id: 'auth_continuous',
            text: 'Continuous/Adaptive Authentication',
            level: 'OPTIMAL',
            productCategory: 'mfa',
            requirements: ['Real-time risk scoring', 'ML-driven anomaly detection', 'Step-up auth on risk']
          }
        ]
      },
      {
        code: 'IDENTITY_STORES',
        name: 'Identity Stores',
        icon: KeyRound,
        description: 'Where identity data lives',
        capabilities: [
          { id: 'id_cloud', text: 'Cloud Identity Provider', level: 'INITIAL', productCategory: 'idp', requirements: ['Cloud IdP deployed', 'Automated provisioning', 'Central password policies'] },
          { id: 'id_unified', text: 'Unified Identity Source', level: 'ADVANCED', productCategory: 'idp', requirements: ['Single authoritative source', 'No shadow IT', 'Auto-deprovisioning < 24hrs'] },
          { id: 'id_federated', text: 'Full Federation', level: 'OPTIMAL', productCategory: 'idp', requirements: ['SAML/OIDC for all apps', 'No local passwords', 'JIT provisioning'] }
        ]
      },
      {
        code: 'ACCESS',
        name: 'Access Management',
        icon: UserCog,
        description: 'How access is controlled',
        capabilities: [
          { id: 'access_sso', text: 'Single Sign-On (SSO)', level: 'INITIAL', productCategory: 'sso', requirements: ['SSO for 80%+ apps', 'Centralized portal', 'Session policies'] },
          { id: 'access_context', text: 'Context-Aware Access', level: 'ADVANCED', productCategory: 'ztna', requirements: ['Device posture checks', 'Location-based policies', 'Risk-based conditional access'] },
          { id: 'access_jit', text: 'Just-In-Time Access', level: 'OPTIMAL', productCategory: 'ztna', requirements: ['Time-bound privileges', 'Approval workflows', 'Full audit trail'] }
        ]
      }
    ]
  },
  DEVICES: {
    label: 'Devices',
    icon: Laptop,
    color: 'var(--zt-device)',
    functions: [
      {
        code: 'DEVICE_HEALTH',
        name: 'Device Health',
        icon: MonitorSmartphone,
        description: 'Device compliance',
        capabilities: [
          { id: 'dev_inventory', text: 'Device Inventory & MDM', level: 'INITIAL', productCategory: 'edr', requirements: ['Complete device inventory', 'MDM deployed', 'Remote wipe capable'] },
          { id: 'dev_compliance', text: 'Pre-Access Compliance', level: 'ADVANCED', productCategory: 'edr', requirements: ['Health check before access', 'Patch verification', 'Encryption required'] },
          { id: 'dev_continuous', text: 'Continuous Trust Scoring', level: 'OPTIMAL', productCategory: 'edr', requirements: ['Real-time trust score', 'Dynamic access adjustment', 'Threat intel integration'] }
        ]
      },
      {
        code: 'THREAT_PROTECT',
        name: 'Threat Protection',
        icon: Shield,
        description: 'Endpoint security',
        capabilities: [
          { id: 'threat_edr', text: 'EDR Deployed', level: 'INITIAL', productCategory: 'edr', requirements: ['EDR on all endpoints', 'Centralized alerts', 'IR playbooks'] },
          { id: 'threat_xdr', text: 'XDR Correlation', level: 'ADVANCED', productCategory: 'edr', requirements: ['Cross-domain detection', 'Unified data lake', 'MITRE mapping'] },
          { id: 'threat_auto', text: 'Automated Response', level: 'OPTIMAL', productCategory: 'edr', requirements: ['SOAR playbooks', 'Auto-isolation', 'ML prioritization'] }
        ]
      }
    ]
  },
  NETWORKS: {
    label: 'Networks',
    icon: Network,
    color: 'var(--zt-network)',
    functions: [
      {
        code: 'SEGMENTATION',
        name: 'Segmentation',
        icon: Layers,
        description: 'Network division',
        capabilities: [
          { id: 'seg_zone', text: 'Zone-Based Segmentation', level: 'INITIAL', productCategory: 'ztna', requirements: ['Security zones defined', 'Firewall between zones', 'DMZ implemented'] },
          { id: 'seg_micro', text: 'Micro-Segmentation', level: 'ADVANCED', productCategory: 'ztna', requirements: ['Workload-level policies', 'East-west inspection', 'App-aware rules'] },
          { id: 'seg_dynamic', text: 'Software-Defined Segmentation', level: 'OPTIMAL', productCategory: 'ztna', requirements: ['Identity-aware networking', 'Dynamic policy updates', 'Full flow visibility'] }
        ]
      },
      {
        code: 'ENCRYPTION',
        name: 'Encryption',
        icon: Lock,
        description: 'Data in transit',
        capabilities: [
          { id: 'enc_external', text: 'External TLS', level: 'INITIAL', requirements: ['TLS 1.2+ everywhere', 'Valid certificates', 'Legacy protocols disabled'] },
          { id: 'enc_internal', text: 'Internal Encryption', level: 'ADVANCED', requirements: ['Service-to-service TLS', 'Database encryption', 'mTLS where applicable'] },
          { id: 'enc_e2e', text: 'End-to-End Encryption', level: 'OPTIMAL', requirements: ['All traffic encrypted', 'HSM key management', 'Zero trusted zones'] }
        ]
      }
    ]
  },
  APPLICATIONS: {
    label: 'Apps',
    icon: Layers,
    color: 'var(--zt-application)',
    functions: [
      {
        code: 'APP_ACCESS',
        name: 'App Access',
        icon: Globe,
        description: 'Application access',
        capabilities: [
          { id: 'app_sso', text: 'SSO for Applications', level: 'INITIAL', productCategory: 'sso', requirements: ['SSO integration', 'SAML/OIDC protocols', 'App catalog'] },
          { id: 'app_ztna', text: 'Zero Trust Network Access', level: 'ADVANCED', productCategory: 'ztna', requirements: ['VPN replaced with ZTNA', 'Per-app access', 'Device posture required'] },
          { id: 'app_continuous', text: 'Continuous Validation', level: 'OPTIMAL', productCategory: 'ztna', requirements: ['Session re-validation', 'Real-time revocation', 'App-level anomaly detection'] }
        ]
      },
      {
        code: 'APP_SECURITY',
        name: 'App Security',
        icon: ShieldCheck,
        description: 'Securing apps',
        capabilities: [
          { id: 'app_scan', text: 'Vulnerability Scanning', level: 'INITIAL', requirements: ['Regular scans', 'SAST in CI/CD', 'CVE tracking'] },
          { id: 'app_devsec', text: 'DevSecOps Pipeline', level: 'ADVANCED', requirements: ['Security gates', 'Container scanning', 'IaC scanning'] },
          { id: 'app_runtime', text: 'Runtime Protection', level: 'OPTIMAL', requirements: ['RASP/WAF', 'API monitoring', 'Real-time blocking'] }
        ]
      }
    ]
  },
  DATA: {
    label: 'Data',
    icon: Database,
    color: 'var(--zt-data)',
    functions: [
      {
        code: 'DATA_PROTECTION',
        name: 'Data Protection',
        icon: FileKey,
        description: 'Protecting data',
        capabilities: [
          { id: 'data_classify', text: 'Data Classification', level: 'INITIAL', productCategory: 'dlp', requirements: ['Classification schema', 'Discovery tools', 'User training'] },
          { id: 'data_dlp', text: 'DLP Enforcement', level: 'ADVANCED', productCategory: 'dlp', requirements: ['Endpoint DLP', 'Cloud DLP', 'Email DLP'] },
          { id: 'data_rights', text: 'Automated Rights Management', level: 'OPTIMAL', productCategory: 'dlp', requirements: ['Auto-encryption', 'Persistent protection', 'Revocation capability'] }
        ]
      },
      {
        code: 'DATA_ACCESS',
        name: 'Data Access',
        icon: FolderLock,
        description: 'Access control',
        capabilities: [
          { id: 'da_encrypt', text: 'Encryption at Rest', level: 'INITIAL', requirements: ['All data encrypted', 'Cloud encryption', 'Backup encryption'] },
          { id: 'da_audit', text: 'Access Auditing', level: 'ADVANCED', productCategory: 'siem', requirements: ['Central logging', 'Anomaly alerts', 'Retention policies'] },
          { id: 'da_realtime', text: 'Real-Time Governance', level: 'OPTIMAL', productCategory: 'siem', requirements: ['Real-time decisions', 'ABAC', 'Dynamic masking'] }
        ]
      }
    ]
  }
};

const CROSS_CUTTING = {
  VISIBILITY: { label: 'Visibility', icon: Eye },
  AUTOMATION: { label: 'Automation', icon: Settings },
  GOVERNANCE: { label: 'Governance', icon: Shield }
};

function deriveLevel(answers) {
  const levelOrder = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
  let highestLevel = 0;
  Object.entries(answers).forEach(([id, data]) => {
    if (data?.enabled) {
      Object.values(PILLAR_ASSESSMENTS).forEach(pillar => {
        pillar.functions.forEach(fn => {
          const cap = fn.capabilities.find(c => c.id === id);
          if (cap) {
            const idx = levelOrder.indexOf(cap.level);
            if (idx > highestLevel) highestLevel = idx;
          }
        });
      });
    }
  });
  return levelOrder[highestLevel];
}

function getUpgradeGuidance(product, targetLevel) {
  const guidance = UPGRADE_GUIDANCE[product] || UPGRADE_GUIDANCE['default'];
  if (targetLevel === 'ADVANCED') return guidance.toAdvanced;
  if (targetLevel === 'OPTIMAL') return guidance.toOptimal;
  return null;
}

// Format spend as compact string with year
function formatSpendCompact(amount) {
  if (!amount || amount <= 0) return null;
  const year = new Date().getFullYear();
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1).replace(/\.0$/, '')}M ${year}`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}k ${year}`;
  return `$${amount} ${year}`;
}

export default function JourneyMapView({
  departmentId,
  assessments = {},
  productAdoptions = {},
  onUpdateLevel,
  onAdoptProduct,
  onRemoveProduct
}) {
  const canvasRef = useRef(null);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [currentFunctionIdx, setCurrentFunctionIdx] = useState(0);
  const [localAnswers, setLocalAnswers] = useState({}); // Local UI state for capabilities
  const [zoom, setZoom] = useState(1);
  const [showInfoFor, setShowInfoFor] = useState(null);
  const [expandedPath, setExpandedPath] = useState(null); // For collapsible upgrade paths

  // Transaction modal state
  const [transactionModal, setTransactionModal] = useState({
    isOpen: false,
    vendor: null,
    product: null,
    capabilityCode: null,
    capabilityName: null
  });

  // Fetch spend indicators and suggestions from CalEProcure data
  const { data: spendIndicators } = useSpendIndicators(departmentId);
  const { fetchTransactions } = useSuggestions(departmentId);

  // Fetch ZT product catalog from API (with fallback to local UPGRADE_GUIDANCE)
  const { products: ztProducts } = useZtProductCatalog();

  // Get adopted product for a function from shared state
  const getAdoptedProduct = useCallback((functionCode) => {
    const cisaCode = FUNCTION_CODE_MAP[functionCode] || functionCode;
    const products = productAdoptions[cisaCode] || [];
    // Return first IN_USE product, or first product
    const inUse = products.find(p => p.status === 'IN_USE');
    return inUse?.productName || products[0]?.productName || null;
  }, [productAdoptions]);

  // Handle product adoption - syncs to shared state
  const handleProductAdopt = useCallback((functionCode, productName) => {
    const cisaCode = FUNCTION_CODE_MAP[functionCode] || functionCode;
    if (onAdoptProduct && productName) {
      onAdoptProduct(cisaCode, productName, 'IN_USE');
    }
  }, [onAdoptProduct]);

  const canvasWidth = 1100;
  const canvasHeight = 420;

  const pillarPositions = {
    IDENTITY: { x: 60, y: 100 },
    DEVICES: { x: 260, y: 100 },
    NETWORKS: { x: 460, y: 100 },
    APPLICATIONS: { x: 660, y: 100 },
    DATA: { x: 860, y: 100 }
  };

  const pillarData = expandedPillar ? PILLAR_ASSESSMENTS[expandedPillar] : null;
  const currentFunction = pillarData?.functions[currentFunctionIdx];

  const derivedLevel = useMemo(() => {
    if (!currentFunction) return 'TRADITIONAL';
    const fnAnswers = {};
    currentFunction.capabilities.forEach(cap => {
      if (localAnswers[cap.id]) fnAnswers[cap.id] = localAnswers[cap.id];
    });
    return deriveLevel(fnAnswers);
  }, [localAnswers, currentFunction]);

  // Get selected product for current function (returns display name)
  const getSelectedProduct = () => {
    if (!currentFunction) return null;
    // First check shared state
    const sharedProd = getAdoptedProduct(currentFunction.code);
    if (sharedProd) {
      const { product } = parseProductValue(sharedProd);
      return product;
    }
    // Fall back to local state
    for (const cap of currentFunction.capabilities) {
      if (localAnswers[cap.id]?.enabled && localAnswers[cap.id]?.product) {
        const { product } = parseProductValue(localAnswers[cap.id].product);
        return product;
      }
    }
    return null;
  };

  const selectedProduct = getSelectedProduct();

  // Build upgrade guidance lookup from API products (when available)
  // Falls back to local UPGRADE_GUIDANCE if API data not loaded
  const getUpgradeGuidanceWithApi = useCallback((productName, targetLevel) => {
    // Try to find product in API catalog first
    const apiProduct = ztProducts?.find(p => p.name === productName);

    // If we have API product data with configurations, derive guidance from it
    // (Future: backend will provide full upgrade guidance via separate API)
    // For now, fall back to local UPGRADE_GUIDANCE which has detailed steps

    const localGuidance = UPGRADE_GUIDANCE[productName] || UPGRADE_GUIDANCE['default'];
    if (targetLevel === 'ADVANCED') return localGuidance.toAdvanced;
    if (targetLevel === 'OPTIMAL') return localGuidance.toOptimal;
    return null;
  }, [ztProducts]);

  // Get ALL higher levels with guidance
  const getUpgradePaths = () => {
    const levelOrder = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
    const currentIdx = levelOrder.indexOf(derivedLevel);
    const paths = [];

    for (let i = currentIdx + 1; i < levelOrder.length; i++) {
      const targetLevel = levelOrder[i];
      const guidance = selectedProduct
        ? getUpgradeGuidanceWithApi(selectedProduct, targetLevel)
        : UPGRADE_GUIDANCE['default'][targetLevel === 'ADVANCED' ? 'toAdvanced' : 'toOptimal'];
      paths.push({ level: targetLevel, guidance });
    }
    return paths;
  };

  const upgradePaths = getUpgradePaths();

  const handlePillarClick = useCallback((pillarKey) => {
    setExpandedPillar(expandedPillar === pillarKey ? null : pillarKey);
    setCurrentFunctionIdx(0);
    setShowInfoFor(null);
  }, [expandedPillar]);

  const handleCapabilityToggle = useCallback((capId, e) => {
    e.stopPropagation();
    setLocalAnswers(prev => ({
      ...prev,
      [capId]: prev[capId]?.enabled ? { ...prev[capId], enabled: false } : { enabled: true }
    }));
  }, []);

  const handleProductChange = useCallback((capId, product, functionCode, e) => {
    e.stopPropagation();
    setLocalAnswers(prev => ({ ...prev, [capId]: { ...prev[capId], product } }));
    // Sync to shared state
    if (functionCode) {
      handleProductAdopt(functionCode, product);
    }
  }, [handleProductAdopt]);

  const handleShowInfo = useCallback((capId, e) => {
    e.stopPropagation();
    setShowInfoFor(showInfoFor === capId ? null : capId);
  }, [showInfoFor]);

  const nextFunction = useCallback((e) => {
    e.stopPropagation();
    if (!pillarData) return;
    if (currentFunctionIdx < pillarData.functions.length - 1) {
      setCurrentFunctionIdx(i => i + 1);
      setShowInfoFor(null);
    } else {
      if (onUpdateLevel) {
        pillarData.functions.forEach(fn => {
          const fnAnswers = {};
          fn.capabilities.forEach(cap => {
            if (localAnswers[cap.id]) fnAnswers[cap.id] = localAnswers[cap.id];
          });
          onUpdateLevel(fn.code, deriveLevel(fnAnswers));
        });
      }
      setExpandedPillar(null);
      setCurrentFunctionIdx(0);
    }
  }, [pillarData, currentFunctionIdx, localAnswers, onUpdateLevel]);

  const prevFunction = useCallback((e) => {
    e.stopPropagation();
    if (currentFunctionIdx > 0) setCurrentFunctionIdx(i => i - 1);
    setShowInfoFor(null);
  }, [currentFunctionIdx]);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    if (onUpdateLevel && pillarData) {
      pillarData.functions.forEach(fn => {
        const fnAnswers = {};
        fn.capabilities.forEach(cap => {
          if (localAnswers[cap.id]) fnAnswers[cap.id] = localAnswers[cap.id];
        });
        onUpdateLevel(fn.code, deriveLevel(fnAnswers));
      });
    }
    setExpandedPillar(null);
    setCurrentFunctionIdx(0);
  }, [onUpdateLevel, pillarData, localAnswers]);

  const getLevelMeta = (levelKey) => MATURITY_LEVELS.find(l => l.key === levelKey);

  return (
    <div className="jmv-container">
      <div className="jmv-top-bar">
        <div className="jmv-title"><Shield size={18} /><span>Zero Trust Assessment</span></div>
        <div className="jmv-legend">
          {MATURITY_LEVELS.map(level => (
            <div key={level.key} className="jmv-legend-item">
              <span className="jmv-legend-dot" style={{ background: level.color }} />
              <span>{level.label}</span>
            </div>
          ))}
        </div>
        <div className="jmv-controls">
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.2))}><ZoomOut size={14} /></button>
          <span>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(2, z + 0.2))}><ZoomIn size={14} /></button>
          <button onClick={() => { setZoom(1); setExpandedPillar(null); }}><RotateCcw size={14} /></button>
        </div>
      </div>

      <div className="jmv-canvas-area">
        <div ref={canvasRef} className={`jmv-canvas ${expandedPillar ? 'jmv-canvas--expanded' : ''}`}
          style={{ transform: `scale(${zoom})`, width: canvasWidth, height: canvasHeight }}>

          <svg className="jmv-grid" width={canvasWidth} height={canvasHeight}>
            <defs>
              <pattern id="jmv-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="10" cy="10" r="1" fill="var(--canvas-grid-color)" opacity="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#jmv-grid)" />
          </svg>

          {!expandedPillar && (
            <>
              <svg className="jmv-edges" width={canvasWidth} height={canvasHeight}>
                <defs>
                  <marker id="arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="var(--color-border)" />
                  </marker>
                </defs>
                {Object.keys(PILLAR_ASSESSMENTS).slice(0, -1).map((key, i) => {
                  const keys = Object.keys(PILLAR_ASSESSMENTS);
                  const p1 = pillarPositions[key];
                  const p2 = pillarPositions[keys[i + 1]];
                  return <line key={`conn-${key}`} x1={p1.x + 180} y1={p1.y + 100} x2={p2.x} y2={p2.y + 100} stroke="var(--color-border)" strokeWidth={2} markerEnd="url(#arrow)" />;
                })}
              </svg>
              <div className="jmv-actor" style={{ left: 10, top: 170 }}><Users size={24} /><span>Users</span></div>
              <div className="jmv-actor" style={{ right: 10, top: 170 }}><Server size={24} /><span>Resources</span></div>
            </>
          )}

          {Object.entries(PILLAR_ASSESSMENTS).map(([key, pillar]) => {
            const pos = pillarPositions[key];
            const isExpanded = expandedPillar === key;
            const isOther = expandedPillar && expandedPillar !== key;
            const PillarIcon = pillar.icon;

            return (
              <div key={key}
                className={`jmv-pillar ${isExpanded ? 'jmv-pillar--expanded' : ''} ${isOther ? 'jmv-pillar--faded' : ''}`}
                style={{ '--pillar-color': pillar.color, '--pillar-x': `${pos.x}px`, '--pillar-y': `${pos.y}px` }}
                onClick={() => !isExpanded && handlePillarClick(key)}>

                {!isExpanded && (
                  <>
                    <div className="jmv-pillar__header"><PillarIcon size={16} /><span>{pillar.label}</span></div>
                    <div className="jmv-pillar__functions">
                      {pillar.functions.map(fn => (
                        <div key={fn.code} className="jmv-pillar__fn">
                          <span className="jmv-pillar__fn-dot" /><span>{fn.name}</span>
                        </div>
                      ))}
                    </div>
                    <div className="jmv-pillar__hint">Click to assess</div>
                  </>
                )}

                {isExpanded && currentFunction && (
                  <div className="jmv-assess">
                    <button className="jmv-assess__close" onClick={handleClose}><X size={16} /></button>

                    <div className="jmv-assess__header">
                      <PillarIcon size={18} />
                      <span>{pillar.label}: {currentFunction.name}</span>
                      <div className="jmv-assess__level" style={{ background: getLevelMeta(derivedLevel)?.color }}>
                        {getLevelMeta(derivedLevel)?.label}
                      </div>
                      <div className="jmv-assess__progress">
                        {pillar.functions.map((fn, i) => (
                          <span key={fn.code} className={`jmv-assess__pip ${i === currentFunctionIdx ? 'active' : ''} ${i < currentFunctionIdx ? 'done' : ''}`} />
                        ))}
                      </div>
                    </div>

                    <div className="jmv-assess__body">
                      <div className="jmv-assess__left">
                        <div className="jmv-assess__section-title">Current Capabilities</div>
                        {currentFunction.capabilities.map(cap => {
                          const capAnswer = localAnswers[cap.id];
                          const isEnabled = capAnswer?.enabled;
                          const levelMeta = getLevelMeta(cap.level);
                          const suggestions = cap.productCategory ? PRODUCT_SUGGESTIONS[cap.productCategory] : [];
                          const isShowingInfo = showInfoFor === cap.id;

                          return (
                            <div key={cap.id} className={`jmv-assess__cap ${isEnabled ? 'jmv-assess__cap--enabled' : ''}`}>
                              <div className="jmv-assess__cap-row" onClick={(e) => handleCapabilityToggle(cap.id, e)}>
                                {isEnabled ? <CheckCircle size={18} className="jmv-check--on" /> : <Circle size={18} className="jmv-check--off" />}
                                <span className="jmv-assess__cap-text">{cap.text}</span>
                                <span className="jmv-assess__cap-badge" style={{ color: levelMeta?.color }}>{levelMeta?.label}</span>
                                <button className="jmv-assess__cap-info-btn" onClick={(e) => handleShowInfo(cap.id, e)}>
                                  <HelpCircle size={14} />
                                </button>
                              </div>
                              {isShowingInfo && (
                                <div className="jmv-assess__cap-reqs">
                                  {cap.requirements?.map((r, i) => <div key={i}>• {r}</div>)}
                                </div>
                              )}
                              {isEnabled && suggestions.length > 0 && (() => {
                                // Prefer shared state, fall back to local
                                const sharedProd = getAdoptedProduct(currentFunction.code);
                                const selectedValue = sharedProd || capAnswer?.product;
                                // Parse the structured value for spend lookup
                                const { vendor: selVendor, product: selProduct } = parseProductValue(selectedValue);
                                // Try product-level first, fall back to vendor-level
                                // (CalEProcure often only has vendor names like "Cisco" not product names like "Duo")
                                const indicator = selVendor ? (
                                  selectVendorIndicator(spendIndicators, selVendor, selProduct, { requireProductMatch: true }) ||
                                  selectVendorIndicator(spendIndicators, selVendor, null, { requireProductMatch: false })
                                ) : null;
                                const spendText = indicator ? formatSpendCompact(indicator.totalSpend) : null;
                                const isVendorLevel = indicator && !indicator.isProductLevel;

                                return (
                                  <div className="jmv-assess__cap-product">
                                    <Package size={12} />
                                    <select value={selectedValue || ''} onChange={(e) => handleProductChange(cap.id, e.target.value, currentFunction.code, e)} onClick={(e) => e.stopPropagation()}>
                                      <option value="">Select product...</option>
                                      {suggestions.map(p => {
                                        // Try product-level first, fall back to vendor-level
                                        const pIndicator = selectVendorIndicator(spendIndicators, p.vendor, p.product, { requireProductMatch: true }) ||
                                                          selectVendorIndicator(spendIndicators, p.vendor, null, { requireProductMatch: false });
                                        const pSpend = pIndicator ? formatSpendCompact(pIndicator.totalSpend) : null;
                                        return <option key={p.value} value={p.value}>{p.display}{pSpend ? ` (${pSpend})` : ''}</option>;
                                      })}
                                    </select>
                                    {spendText && (
                                      <>
                                        <span className={`jmv-assess__spend-badge ${isVendorLevel ? 'jmv-assess__spend-badge--vendor' : ''}`}>
                                          ({spendText}{isVendorLevel ? '*' : ''})
                                        </span>
                                        <button
                                          className="jmv-assess__spend-info"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTransactionModal({
                                              isOpen: true,
                                              vendor: selVendor,
                                              product: selProduct,
                                              capabilityCode: cap.productCategory,
                                              capabilityName: cap.text
                                            });
                                          }}
                                          title={isVendorLevel ? `View ${selVendor} transactions (vendor total)` : 'View CalEProcure transactions'}
                                        >
                                          <Info size={12} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>

                      <div className="jmv-assess__right">
                        <div className="jmv-assess__section-title">
                          <Zap size={14} /> Upgrade Path {selectedProduct && <span className="jmv-assess__product-tag">{selectedProduct}</span>}
                        </div>

                        {upgradePaths.length === 0 ? (
                          <div className="jmv-assess__optimal-reached">
                            <CheckCircle size={16} />
                            <span>Optimal level achieved!</span>
                          </div>
                        ) : (
                          <div className="jmv-assess__paths">
                            {upgradePaths.map(({ level, guidance }) => {
                              const isExpanded = expandedPath === level;
                              const levelMeta = getLevelMeta(level);
                              return (
                                <div key={level} className={`jmv-assess__path ${isExpanded ? 'jmv-assess__path--open' : ''}`}>
                                  <button
                                    className="jmv-assess__path-header"
                                    onClick={(e) => { e.stopPropagation(); setExpandedPath(isExpanded ? null : level); }}
                                  >
                                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    <span className="jmv-assess__path-level" style={{ color: levelMeta?.color }}>{levelMeta?.label}</span>
                                    {guidance?.alreadyMeets && <span className="jmv-assess__tag jmv-assess__tag--success">✓ Ready</span>}
                                    {guidance?.needsProductChange && <span className="jmv-assess__tag jmv-assess__tag--warn">Change Req'd</span>}
                                    {guidance?.additionalProducts && <span className="jmv-assess__tag jmv-assess__tag--info">+Products</span>}
                                  </button>

                                  {isExpanded && (
                                    <div className="jmv-assess__path-body">
                                      {guidance?.effort && (
                                        <div className="jmv-assess__path-effort">{guidance.effort}</div>
                                      )}
                                      <div className="jmv-assess__path-steps">
                                        {guidance?.steps?.map((step, i) => (
                                          <div key={i} className="jmv-assess__path-step">
                                            <span className="jmv-assess__step-dot" />{step}
                                          </div>
                                        ))}
                                      </div>
                                      {guidance?.additionalProducts && (
                                        <div className="jmv-assess__path-products">
                                          <Package size={12} /> {guidance.additionalProducts.join(', ')}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="jmv-assess__footer">
                      <button className="jmv-assess__btn jmv-assess__btn--secondary" onClick={prevFunction} disabled={currentFunctionIdx === 0}>
                        <ArrowLeft size={14} /> Prev
                      </button>
                      <span className="jmv-assess__footer-info">{currentFunctionIdx + 1} / {pillar.functions.length}</span>
                      <button className="jmv-assess__btn jmv-assess__btn--primary" onClick={nextFunction}>
                        {currentFunctionIdx < pillar.functions.length - 1 ? <>Next <ArrowRight size={14} /></> : <>Done <Check size={14} /></>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!expandedPillar && (
            <div className="jmv-crosscut">
              {Object.entries(CROSS_CUTTING).map(([key, cc]) => (
                <div key={key} className="jmv-crosscut__item">
                  {React.createElement(cc.icon, { size: 14 })}<span>{cc.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="jmv-status">
        {!expandedPillar ? <span>Click a pillar to assess</span> : <span>{pillarData?.label} → {currentFunction?.name}</span>}
      </div>

      {/* Transaction Details Modal */}
      <TransactionListModal
        isOpen={transactionModal.isOpen}
        onClose={() => setTransactionModal({ isOpen: false, vendor: null, product: null, capabilityCode: null, capabilityName: null })}
        capabilityCode={transactionModal.capabilityCode}
        capabilityName={transactionModal.capabilityName}
        vendor={transactionModal.vendor}
        product={transactionModal.product}
        fetchTransactions={fetchTransactions}
      />
    </div>
  );
}
